import { TextField, type TextFieldProps } from '@arionbanki/arionui';
import { isKennitala } from 'helpers';
import { AppContext } from 'providers/AppContext';
import { useContext, useEffect, useState } from 'react';
import useSWR from 'swr';

type NationalRegistryResponce = {
	gender: number;
	isAlive: boolean;
	isCompany: boolean;
	isOfLegalAge: boolean;
	kennitala: null | string;
	name: null | string;
	address: null | {
		city: string;
		country: string;
		home: string;
		postalCode: string;
	};
};

type SSNInputProps = TextFieldProps & {
	validate?: (kennitala: string) => string | undefined;
};

export function SSNInput({ validate, ...props }: Readonly<SSNInputProps>) {
	const { defaultValue } = props;
	const { config, t } = useContext(AppContext);
	const { API } = config;
	const [error, setError] = useState(!!props.error);
	const [message, setMessage] = useState('');
	const defaultURL = isKennitala(defaultValue) ? `${API.nationalRegistry}/${defaultValue}` : null;
	const [url, setURL] = useState<string | null>(defaultURL);
	const { data } = useSWR<NationalRegistryResponce>(url);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (isKennitala(value)) {
			const validationMessage = validate?.(value);
			if (validationMessage) {
				setURL(null);
				setMessage(validationMessage);
				setError(true);
			} else {
				setURL(`${API.nationalRegistry}/${value}`);
				setMessage('');
				setError(false);
			}
		} else {
			setURL(null);
			setMessage('');
			setError(false);
		}
	};

	useEffect(() => {
		if (!data) return;

		if (!data.kennitala) {
			setMessage(t('error_ssn_not_found', 'mandate'));
			return setError(true);
		}

		if (data.isCompany) {
			setMessage(t('error_company', 'mandate'));
			return setError(true);
		}

		if (data.isOfLegalAge === false) {
			setMessage(t('error_under_18', 'mandate'));
			return setError(true);
		}

		if (data.name) {
			setMessage(data.name);
			setError(false);
		}
	}, [data]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		props.error && setError(true);
	}, [props.error]);

	return <TextField {...props} error={error} message={message} onChange={handleChange} />;
}
